import React, { useState, useEffect, useRef } from "react";

import { Button, Modal, Select, Spin, Tag, Tooltip } from "antd";
import ReactQuill, { Quill } from "react-quill-new";

import "react-quill/dist/quill.snow.css";
import Iframe from "react-iframe";
import useToast from "../../../hooks/userToast";
import { useParams } from "react-router-dom";
import apiClient from "../../../utils/apiClient";
import QuillTableBetter from "quill-table-better";
import useUser from "../../../hooks/useUser";
import { useTenant } from "../../../context/TenantContext";

import {
  ArrowLeftOutlined,
  AudioOutlined,
  BulbOutlined,
  CalendarOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  IdcardOutlined,
  MedicineBoxOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WarningOutlined,
  WechatOutlined,
} from "@ant-design/icons";
import StudyTextModal from "../Studies/StudyTextModal";
import {
  checkCritical,
  genImpression,
  structureReport,
  transcribeAudio,
} from "../../../services/ai-client";

Quill.register({ "modules/table-better": QuillTableBetter }, true);

// ─── Modality colour map ──────────────────────────────────────────────────────
const MODALITY_COLORS = {
  CT:   { bg: "#e6f4ff", color: "#0958d9", border: "#91caff" },
  MRI:  { bg: "#f9f0ff", color: "#722ed1", border: "#d3adf7" },
  MR:   { bg: "#f9f0ff", color: "#722ed1", border: "#d3adf7" },
  XR:   { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f" },
  CR:   { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f" },
  DX:   { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f" },
  US:   { bg: "#fff7e6", color: "#d46b08", border: "#ffd591" },
  NM:   { bg: "#fff1f0", color: "#cf1322", border: "#ffa39e" },
  PET:  { bg: "#fff1f0", color: "#cf1322", border: "#ffa39e" },
  MG:   { bg: "#e6fffb", color: "#08979c", border: "#87e8de" },
  FL:   { bg: "#fffbe6", color: "#ad6800", border: "#ffe58f" },
};

function getModalityStyle(modality) {
  const key = (modality || "").toUpperCase().trim();
  return MODALITY_COLORS[key] || { bg: "#f0f0f0", color: "#595959", border: "#d9d9d9" };
}

// ─── Patient info header card ─────────────────────────────────────────────────
function PatientInfoCard({ study, userDetails, role }) {
  if (!study) {
    return (
      <div style={{ padding: "12px 16px", background: "#f5f5f5", borderRadius: 8, marginBottom: 12 }}>
        <div style={{ color: "#999", fontSize: 13 }}>Loading patient info…</div>
      </div>
    );
  }

  const modality =
    study.modality || study.modality_name || study.ModalityType || "";
  const modalityStyle = getModalityStyle(modality);

  const fields = [
    { icon: <UserOutlined />,         label: "Patient",   value: study.patient_name },
    { icon: <IdcardOutlined />,       label: "Age / Sex", value: [study.age, study.gender].filter(Boolean).join(" / ") },
    { icon: <MedicineBoxOutlined />,  label: "Modality",  value: modality, isModality: true },
    { icon: <CalendarOutlined />,     label: "Study Date",value: study.study_date || study.createdAt?.split?.("T")?.[0] || "—" },
  ];

  if (study.referring_doctor || study.referral_doctor) {
    fields.push({
      icon: <UserOutlined />,
      label: "Ref. Doctor",
      value: study.referring_doctor || study.referral_doctor,
    });
  }
  if (study.clinical_history || study.history) {
    fields.push({
      icon: <MedicineBoxOutlined />,
      label: "History",
      value: study.clinical_history || study.history,
    });
  }

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        borderRadius: 10,
        padding: "12px 16px",
        marginBottom: 12,
        border: "1px solid #334155",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Patient Information
        </span>
        {modality && (
          <span
            style={{
              fontSize: 12, fontWeight: 700,
              background: modalityStyle.bg,
              color: modalityStyle.color,
              border: `1px solid ${modalityStyle.border}`,
              borderRadius: 4, padding: "2px 10px",
              letterSpacing: "0.05em",
            }}
          >
            {modality}
          </span>
        )}
      </div>

      {/* Info grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
        {fields.map(({ icon, label, value, isModality }) =>
          isModality ? null : (
            <div key={label} style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {label}
              </span>
              <span
                style={{
                  fontSize: 13, fontWeight: 600, color: "#e2e8f0",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
                title={value || "—"}
              >
                {value || "—"}
              </span>
            </div>
          )
        )}
      </div>

      {/* Clinical history — full width if present */}
      {(study.clinical_history || study.history) && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #334155" }}>
          <span style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 2 }}>
            Clinical History
          </span>
          <span style={{ fontSize: 12, color: "#cbd5e1", lineHeight: 1.5 }}>
            {study.clinical_history || study.history}
          </span>
        </div>
      )}

      {/* Doctor signature preview */}
      {role === "Doctor" && userDetails?.file_path && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #334155" }}>
          <span style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
            Radiologist Signature
          </span>
          <img src={userDetails.file_path} alt="signature" style={{ maxHeight: 48, objectFit: "contain" }} />
        </div>
      )}
    </div>
  );
}

// ─── Main Viewer ──────────────────────────────────────────────────────────────
const Viewer = () => {
  const { toastSuccess, toastError } = useToast();
  const quillRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const autoSelectedRef = useRef(false);    // prevent re-running auto-select
  const hasExistingContent = useRef(false); // true when saved report loaded
  const [contentFetchDone, setContentFetchDone] = useState(false);
  const { role, isSitePerson } = useUser();
  const { features, ohifViewerUrl: tenantOhifUrl } = useTenant();

  const ohifBaseUrl =
    tenantOhifUrl ||
    process.env.REACT_APP_OHIF_VIEWER_URL ||
    process.env.REACT_APP_BASE_URL_VIWER_URL;

  const [editorValue, setEditorValue] = useState("");
  const [finalHtml, setFinalHtml] = useState("");
  const [templateOptions, setTemplateOptions] = useState([]);
  const [studyDetails, setStudyDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const { studyId, radiologistId, studyInfoId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isViewerFullScreen, setIsViewerFullScreen] = useState(true);
  const [historyModal, setHistoryModal] = useState({ isModalOpen: false, studyKey: "" });
  const [studyRemark, setStudyRemark] = useState();

  // AI state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isStructuring, setIsStructuring] = useState(false);
  const [isGeneratingImpression, setIsGeneratingImpression] = useState(false);
  const [aiStep, setAiStep] = useState(""); // pipeline status label shown under dictate button
  const [criticalModal, setCriticalModal] = useState({ open: false, matches: [], message: "" });

  const handleOkTextModal = () => setHistoryModal({ isModalOpen: false, studyKey: "" });
  const handleCancelTextModal = () => setHistoryModal({ isModalOpen: false, studyKey: "" });

  const modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, "bold", "italic"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      [{ align: [] }],
    ],
    "table-better": { language: "en_US" },
  };

  // ─── Initial data fetch ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await apiClient.get("/auth/templates");
        if (response.data) setTemplateOptions(response.data);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    const fetchRemarks = async () => {
      try {
        const response = await apiClient.get(`/study-remark?studyInfoId=${studyInfoId}`);
        setStudyRemark(response?.data?.StudyRemark?.data);
      } catch (error) {
        console.error("Error fetching remarks:", error);
      }
    };
    fetchTemplates();
    fetchRemarks();
  }, []);

  // ─── Study details, editor content, user details ───────────────────────────
  useEffect(() => {
    const fetchStudyDetails = async () => {
      try {
        const response = await apiClient.get(`/auth/get-study-details/${studyInfoId}`);
        setStudyDetails(response?.data?.studyInfo || null);
      } catch (error) {
        console.error("Error fetching study details:", error);
      }
    };

    const fetchEditorContent = async () => {
      try {
        const response = await apiClient.get(
          `/auth/getLatestWorldUrl?studyInfoId=${studyInfoId}&role=${role}`,
        );
        let content = response?.data?.studyDetail?.html;
        if (content) {
          hasExistingContent.current = true;
          if (isSitePerson && studyDetails?.status !== "final") {
            setEditorValue("");
            return;
          }
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = content;

          // Strip patient table, signature, disclaimer (already added server-side)
          const tables = tempDiv.getElementsByTagName("table");
          if (tables.length > 0) tables[0].remove();
          tempDiv.querySelectorAll("img").forEach((img) => {
            if (img?.src?.includes("signature") || img?.alt?.toLowerCase() === "signature") {
              img.closest("p")?.remove();
              img.remove();
            }
          });
          const disclaimer = Array.from(tempDiv.querySelectorAll("p")).find((p) =>
            p.textContent.includes("Disclaimer")
          );
          if (disclaimer) disclaimer.remove();

          setEditorValue(tempDiv.innerHTML);
        }
      } catch (error) {
        console.error("Error fetching editor content:", error);
      } finally {
        setContentFetchDone(true);
      }
    };

    const fetchUserDetails = async () => {
      try {
        const response = await apiClient.get(`/auth/${radiologistId}/userDetail`);
        setUserDetails(response?.data?.user || null);
      } catch {
        // Non-critical — only used for Doctor signature; silently ignore 404
      }
    };

    fetchStudyDetails();
    fetchEditorContent();
    fetchUserDetails();
  }, [studyDetails?.status]);

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplate(templateId);
    try {
      const url = studyInfoId
        ? `auth/wordToHtml?template_url=${templateId}&studyInfoId=${studyInfoId}`
        : `auth/wordToHtml?template_url=${templateId}`;
      const response = await apiClient.post(url);
      const rawHtml = response.data ? response?.data?.htmlFile?.html : "";
      // Overwrite patient table cells with live studyDetails data
      const filledHtml = fillPatientTable(rawHtml, studyDetails);
      setEditorValue(filledHtml);
    } catch (error) {
      console.error("Error loading template content:", error);
    }
  };

  // ─── Auto-select template by modality ─────────────────────────────────────
  useEffect(() => {
    if (autoSelectedRef.current) return;                  // already done
    if (!contentFetchDone) return;                        // wait until saved-report check is done
    if (!templateOptions?.template?.data?.length) return; // templates not loaded
    if (hasExistingContent.current) return;               // saved report exists — don't override

    const templates = templateOptions.template.data;

    const modality = (
      studyDetails?.modality ||
      studyDetails?.modality_name ||
      studyDetails?.ModalityType ||
      studyDetails?.Modality ||
      ""
    ).toUpperCase();

    // 1. Try to match by modality label (e.g. "CT Brain", "MRI Spine")
    const byModality = modality
      ? templates.find((t) => {
          const label = (t.label || "").toUpperCase();
          return label.includes(modality) || modality.includes(label);
        })
      : null;

    // 2. Fallback: use the first template in the list
    const chosen = byModality || templates[0];

    if (chosen) {
      autoSelectedRef.current = true;
      handleTemplateChange(chosen.value);
    }
  }, [studyDetails, templateOptions, contentFetchDone]); // handleTemplateChange is stable

  const escapeTextNodes = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.nodeValue = node.nodeValue
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      }
      node.childNodes.forEach(walk);
    };
    walk(doc.body);
    return doc.body.innerHTML;
  };

  const getEditorPlainText = () => {
    const quill = quillRef.current?.getEditor();
    return quill ? quill.getText() : editorValue.replace(/<[^>]+>/g, "");
  };

  /**
   * Splits editor HTML into three parts so AI replacements never touch the
   * patient info table (header) or the signature/disclaimer (footer).
   *
   *  headerHtml — first <table> (patient info)
   *  bodyHtml   — report sections (STUDY PROTOCOLS … RECOMMENDATION)
   *  footerHtml — everything after the last recognised section (signature, disclaimer)
   */
  const splitEditorContent = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html || "";

    // ── 1. Pull out patient table ─────────────────────────────────────
    let headerHtml = "";
    const tables = div.getElementsByTagName("table");
    if (tables.length > 0) {
      headerHtml = tables[0].outerHTML;
      tables[0].remove();
    }

    // ── 2. Find footer — everything after the last section keyword ────
    const SECTION_RE = /STUDY PROTOCOLS|FINDINGS|IMPRESSION|RECOMMENDATION/i;
    const children = Array.from(div.children);

    let lastSectionIdx = -1;
    children.forEach((el, idx) => {
      if (SECTION_RE.test(el.textContent)) lastSectionIdx = idx;
    });

    let footerHtml = "";
    if (lastSectionIdx !== -1) {
      const footerStart = lastSectionIdx + 2; // heading + content paragraph
      if (footerStart < children.length) {
        footerHtml = children.slice(footerStart).map((el) => el.outerHTML).join("");
        children.slice(footerStart).forEach((el) => el.remove());
      }
    }

    return { headerHtml, bodyHtml: div.innerHTML, footerHtml };
  };

  /**
   * Injects AI-generated text INTO the existing template section content
   * paragraphs, preserving the template's own headings and formatting.
   *
   * sectionData = { technique, findings, impression, recommendation }
   *
   * If the template body already has section headings (STUDY PROTOCOLS, etc.),
   * the paragraph immediately following each heading is updated in-place.
   * If a section heading is absent, the section is appended at the end.
   */
  const injectIntoSections = (bodyHtml, sectionData) => {
    const div = document.createElement("div");
    div.innerHTML = bodyHtml || "";
    const elements = Array.from(div.children);

    const SECTION_MAP = {
      "STUDY PROTOCOLS": sectionData.technique || "",
      FINDINGS: sectionData.findings || "",
      IMPRESSION: sectionData.impression || "",
      RECOMMENDATION: sectionData.recommendation || "",
    };

    const injected = new Set();

    elements.forEach((el, idx) => {
      const text = el.textContent.replace(/[^A-Z ]/gi, "").toUpperCase().trim();
      for (const [key, content] of Object.entries(SECTION_MAP)) {
        if (text.includes(key) && !injected.has(key)) {
          injected.add(key);
          // Replace the very next sibling paragraph with AI content
          const nextEl = elements[idx + 1];
          if (nextEl && content) {
            nextEl.innerHTML = content;
          } else if (content) {
            // No paragraph found after heading — insert one
            const p = document.createElement("p");
            p.innerHTML = content;
            el.insertAdjacentElement("afterend", p);
          }
          break;
        }
      }
    });

    // Append any sections whose headings were not found in the template
    for (const [key, content] of Object.entries(SECTION_MAP)) {
      if (!injected.has(key) && content) {
        div.innerHTML +=
          `<p><strong>${key}:</strong></p><p>${content}</p>`;
      }
    }

    return div.innerHTML;
  };

  /**
   * Overwrites the patient info table inside template HTML with live data
   * from studyDetails. Matches each label cell (Patient ID, Name, Age, etc.)
   * and fills the adjacent value cell.
   */
  const fillPatientTable = (html, study) => {
    if (!study) return html;
    const div = document.createElement("div");
    div.innerHTML = html;
    const table = div.querySelector("table");
    if (!table) return html;

    const modality =
      study.modality || study.modality_name || study.ModalityType || study.Modality || "";
    const patientName = study.patient_name || study.name || "";
    const age = study.age || "";
    const gender = study.gender || study.sex || "";
    const ageGender = age && gender
      ? `${age} - ${gender.charAt(0).toUpperCase()}`
      : age || gender;
    const studyDate = study.study_date || study.createdAt?.split?.("T")?.[0] || "";
    const patientId = study.patient_id || study.accession_number || study.id || "";
    const studyName =
      study.study_description || study.study_name || study.StudyDescription ||
      (modality ? `${modality} STUDY` : "");
    const referringDoctor = study.referring_doctor || study.referral_doctor || "";

    // Build lookup: label text (uppercase) → replacement value
    const FIELD_MAP = {
      "PATIENT ID": patientId,
      "PT ID":      patientId,
      "NAME":       patientName,
      "AGE":        ageGender,
      "AGE/SEX":    `${age} / ${gender}`,
      "STUDY DATE": studyDate,
      "DATE":       studyDate,
      "STUDY NAME": studyName,
      "STUDY TYPE": studyName,
      "STUDY DESCRIPTION": studyName,
      "REFERRING DOCTOR":  referringDoctor,
      "REF. DOCTOR":       referringDoctor,
      "REFERRED BY":       referringDoctor,
    };

    table.querySelectorAll("tr").forEach((row) => {
      const cells = Array.from(row.querySelectorAll("td, th"));
      // Each row: [label, value, label, value] — process in pairs
      for (let i = 0; i < cells.length - 1; i++) {
        const labelText = cells[i].textContent.trim().toUpperCase();
        for (const [key, val] of Object.entries(FIELD_MAP)) {
          if (labelText.includes(key)) {
            // value cell is the next td/th in the row
            cells[i + 1].textContent = val;
            i++; // skip the value cell we just filled
            break;
          }
        }
      }
    });

    return div.innerHTML;
  };

  // Auto-detect modality from studyDetails (tries all known field names)
  const getModality = () =>
    studyDetails?.modality ||
    studyDetails?.modality_name ||
    studyDetails?.ModalityType ||
    studyDetails?.Modality ||
    "";

  // ─── Core save ────────────────────────────────────────────────────────────
  const performSave = async () => {
    try {
      setIsLoading(true);
      const safeHtml = escapeTextNodes(editorValue);
      const response = await apiClient.post(
        `/auth/convertHtmlToWordAndSave?role_radiologist=${radiologistId}&study_info_model_id=${studyInfoId}`,
        { htmlContent: safeHtml },
      );
      await apiClient.post("auth/create-study-status", {
        study_info_model_id: parseInt(studyInfoId),
        ...(role === "Radiologist" ? { role_radiologist: parseInt(radiologistId) } : {}),
        ...(role === "Qualitychecker" ? { role_quality_checker: parseInt(radiologistId) } : {}),
        word_url: response.data.wordFile.word,
        pdf_url: response.data.wordFile.pdf,
        radio_pdf_url: response.data.wordFile.radio_pdf_url,
        radio_word_url: response.data.wordFile.radio_word_url,
        status: "read",
      });
      toastSuccess("Report saved successfully!");
      window.close();
    } catch (error) {
      console.error("API Error:", error);
      toastError("Something went wrong");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Submit with critical check ────────────────────────────────────────────
  const handleSave = async () => {
    const reportText = getEditorPlainText();
    try {
      const result = await checkCritical(reportText);
      if (result.is_critical) {
        setCriticalModal({
          open: true,
          matches: result.matches || [],
          message: result.message || "Critical findings detected in this report.",
        });
        return;
      }
    } catch (err) {
      console.warn("Critical check failed, proceeding with save:", err.message);
    }
    await performSave();
  };

  // ─── Feature 1: Dictate → auto-structure → auto-impression pipeline ──────
  const handleDictate = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const file = new File([blob], "dictation.webm", { type: mimeType });

        try {
          // ── Step 1: Transcribe ──────────────────────────────────────────
          setIsTranscribing(true);
          setAiStep("Transcribing speech...");
          const transcribed = await transcribeAudio(file);
          const rawText = transcribed.text || "";
          setIsTranscribing(false);

          if (!rawText.trim()) {
            toastError("No speech detected. Please try again.");
            setAiStep("");
            return;
          }

          // ── Step 2: Structure report ────────────────────────────────────
          // Split editor so AI only touches the body, never header/footer
          const { headerHtml, bodyHtml, footerHtml } = splitEditorContent(editorValue);
          const modality = getModality();
          const clinicalHistory = studyDetails?.clinical_history || studyDetails?.history || "";
          setIsStructuring(true);
          setAiStep("Structuring report...");
          const structured = await structureReport({ raw_text: rawText, modality, clinical_history: clinicalHistory });
          setIsStructuring(false);

          // ── Step 3: Generate Impression from structured findings ────────
          setIsGeneratingImpression(true);
          setAiStep("Generating impression...");
          const findingsText = structured.findings || rawText;
          const impressionResult = await genImpression({ findings: findingsText, modality });
          const impressionText = impressionResult.impression || "";
          setIsGeneratingImpression(false);

          // ── Step 4: Inject AI content INTO existing template sections ──
          // This preserves the template's own section headings and formatting
          const newBody = injectIntoSections(bodyHtml, {
            technique: structured.technique || "",
            findings: structured.findings || rawText,
            impression: impressionText,
            recommendation: structured.recommendation || "",
          });

          const finalHtmlContent = headerHtml + newBody + footerHtml;
          setEditorValue(finalHtmlContent);
          setFinalHtml(finalHtmlContent);
          setAiStep("");
          toastSuccess("Report structured and impression generated!");

        } catch (err) {
          toastError("AI pipeline failed: " + err.message);
          setAiStep("");
        } finally {
          setIsTranscribing(false);
          setIsStructuring(false);
          setIsGeneratingImpression(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      toastError("Microphone access denied: " + err.message);
    }
  };

  // ─── Feature 2: AI Structure ───────────────────────────────────────────────
  const handleAiStructure = async () => {
    const rawText = getEditorPlainText();
    const modality = getModality();
    const clinicalHistory = studyDetails?.clinical_history || studyDetails?.history || "";
    const { headerHtml, bodyHtml, footerHtml } = splitEditorContent(editorValue);
    try {
      setIsStructuring(true);
      const result = await structureReport({ raw_text: rawText, modality, clinical_history: clinicalHistory });
      const newBody = injectIntoSections(bodyHtml, {
        technique: result.technique || "",
        findings: result.findings || "",
        impression: result.impression || "",
        recommendation: result.recommendation || "",
      });
      const newHtml = headerHtml + newBody + footerHtml;
      setEditorValue(newHtml);
      setFinalHtml(newHtml);
      toastSuccess("Report structured by AI!");
    } catch (err) {
      toastError("AI Structuring failed: " + err.message);
    } finally {
      setIsStructuring(false);
    }
  };

  // ─── Feature 3: AI Impression ──────────────────────────────────────────────
  const handleAiImpression = async () => {
    const fullText = getEditorPlainText();
    const findingsMatch = fullText.match(/FINDINGS[\s\S]*?(?=IMPRESSION|RECOMMENDATION|$)/i);
    const findingsText = findingsMatch
      ? findingsMatch[0].replace(/^FINDINGS\s*/i, "").trim()
      : fullText;
    const modality = getModality();
    try {
      setIsGeneratingImpression(true);
      const result = await genImpression({ findings: findingsText, modality });
      const impressionHtml = `<p><strong>IMPRESSION</strong></p><p>${result.impression}</p>`;
      const impressionPattern = /(<p><strong>IMPRESSION<\/strong><\/p>)(<p>[\s\S]*?<\/p>)?/i;
      if (impressionPattern.test(editorValue)) {
        setEditorValue(editorValue.replace(impressionPattern, impressionHtml));
      } else {
        const updated = editorValue + impressionHtml;
        setEditorValue(updated);
        setFinalHtml(updated);
      }
      toastSuccess("AI Impression generated!");
    } catch (err) {
      toastError("AI Impression failed: " + err.message);
    } finally {
      setIsGeneratingImpression(false);
    }
  };

  // ─── OHIF message listener ─────────────────────────────────────────────────
  useEffect(() => {
    const handleOhifMessage = (event) => {
      try {
        const allowedOrigin = process.env.REACT_APP_OHIF_VIEWER_ORIGIN || "http://localhost:3000";
        if (allowedOrigin && event.origin !== allowedOrigin) return;
        const data = event.data;
        if (!data || data.source !== "ohif") return;
        if (data.type === "REPORT_SNIPPET" && data.payload?.text) {
          const snippet = `<p>${data.payload.text}</p>`;
          setEditorValue((prev) => (prev || "") + snippet);
          setFinalHtml((prev) => (prev || "") + snippet);
        }
      } catch (e) {
        console.error("Error handling OHIF message", e);
      }
    };
    window.addEventListener("message", handleOhifMessage);
    return () => window.removeEventListener("message", handleOhifMessage);
  }, []);

  const disableButton = role === "Radiologist" || role === "Qualitychecker" ? false : true;
  const modality = getModality();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        /* ── Futuristic viewer shell ── */
        .rdy-viewer-root {
          font-family: 'Inter', sans-serif;
          background: #060B14;
          height: 100vh;
          width: 100%;
          display: flex;
          overflow: hidden;
          position: relative;
        }
        .rdy-viewer-dots {
          background-image: radial-gradient(rgba(99,102,241,0.12) 1px, transparent 1px);
          background-size: 24px 24px;
        }

        /* ── Recording pulse ── */
        @keyframes rec-pulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
          50%      { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
        }
        .recording-btn { animation: rec-pulse 1.2s ease-in-out infinite !important; }

        /* ── Ant Design Select override (dark) ── */
        .rdy-select .ant-select-selector {
          background: #0e1629 !important;
          border: 1px solid rgba(99,102,241,0.35) !important;
          color: #e2e8f0 !important;
          border-radius: 6px !important;
        }
        .rdy-select .ant-select-selector:hover,
        .rdy-select.ant-select-focused .ant-select-selector {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 2px rgba(99,102,241,0.2) !important;
        }
        .rdy-select .ant-select-selection-placeholder { color: #475569 !important; }
        .rdy-select .ant-select-arrow { color: #6366f1 !important; }
        .rdy-select .ant-select-selection-item { color: #e2e8f0 !important; }

        /* ── AI button base ── */
        .rdy-ai-btn {
          background: rgba(99,102,241,0.08) !important;
          border: 1px solid rgba(99,102,241,0.35) !important;
          color: #a5b4fc !important;
          border-radius: 6px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          transition: all 0.2s !important;
        }
        .rdy-ai-btn:hover:not(:disabled) {
          background: rgba(99,102,241,0.2) !important;
          border-color: #818cf8 !important;
          color: #c7d2fe !important;
          box-shadow: 0 0 12px rgba(99,102,241,0.3) !important;
        }
        .rdy-ai-btn:disabled { opacity: 0.4 !important; cursor: not-allowed !important; }

        /* ── Dictate recording state ── */
        .rdy-dictate-active {
          background: rgba(239,68,68,0.15) !important;
          border-color: #ef4444 !important;
          color: #fca5a5 !important;
        }

        /* ── Submit button ── */
        .rdy-submit-btn {
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%) !important;
          border: none !important;
          color: white !important;
          font-weight: 600 !important;
          letter-spacing: 0.04em !important;
          border-radius: 8px !important;
          box-shadow: 0 0 20px rgba(99,102,241,0.4) !important;
          transition: all 0.2s !important;
        }
        .rdy-submit-btn:hover:not(:disabled) {
          box-shadow: 0 0 30px rgba(99,102,241,0.6) !important;
        }
        .rdy-submit-btn:disabled { opacity: 0.5 !important; }

        /* ── ReactQuill dark shell ── */
        .rdy-quill .ql-toolbar {
          background: #0a0f1e !important;
          border: 1px solid rgba(99,102,241,0.25) !important;
          border-bottom: 1px solid rgba(99,102,241,0.15) !important;
          border-radius: 8px 8px 0 0 !important;
        }
        .rdy-quill .ql-toolbar button,
        .rdy-quill .ql-toolbar .ql-picker-label { color: #94a3b8 !important; }
        .rdy-quill .ql-toolbar button:hover { color: #818cf8 !important; }
        .rdy-quill .ql-toolbar .ql-stroke { stroke: #94a3b8 !important; }
        .rdy-quill .ql-toolbar button:hover .ql-stroke { stroke: #818cf8 !important; }
        .rdy-quill .ql-toolbar .ql-fill { fill: #94a3b8 !important; }
        .rdy-quill .ql-container {
          background: #ffffff !important;
          border: 1px solid rgba(99,102,241,0.25) !important;
          border-top: none !important;
          border-radius: 0 0 8px 8px !important;
          min-height: 340px !important;
        }

        /* ── Fullscreen toggle btn ── */
        .rdy-fs-btn {
          background: rgba(99,102,241,0.15) !important;
          border: 1px solid rgba(99,102,241,0.4) !important;
          color: #818cf8 !important;
          border-radius: 6px !important;
        }
        .rdy-fs-btn:hover {
          background: rgba(99,102,241,0.28) !important;
          box-shadow: 0 0 12px rgba(99,102,241,0.35) !important;
        }

        /* ── Panel label / section header ── */
        .rdy-section-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #6366f1;
        }
      `}</style>

      {/* ── Root shell ── */}
      <div
        className="rdy-viewer-root rdy-viewer-dots"
        style={{ width: "100vw", height: "100vh", display: "flex", overflow: "hidden", position: "relative", background: "#060B14" }}
      >

        {/* ── Layout row fills 100% of root ── */}
        <div style={{ display: "flex", width: "100%", height: "100%" }}>

          {/* ══ OHIF viewer pane ══ */}
          <div style={{
            flex: isViewerFullScreen ? "1 1 100%" : "0 0 62%",
            height: "100%",
            position: "relative",
            borderRight: isViewerFullScreen ? "none" : "1px solid rgba(99,102,241,0.2)",
          }}>
            {/* Back button — floats top-left */}
            <button
              onClick={() => window.close()}
              title="Close"
              style={{
                position: "absolute", top: 10, left: 10, zIndex: 30,
                background: "rgba(6,11,20,0.75)",
                border: "1px solid rgba(99,102,241,0.4)",
                borderRadius: 7, width: 32, height: 32,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#818cf8",
              }}
            >
              <ArrowLeftOutlined style={{ fontSize: 13 }} />
            </button>

            {/* Toggle — floats top-right */}
            <Tooltip title={isViewerFullScreen ? "Show Report Editor" : "Expand Viewer"}>
              <Button
                className="rdy-fs-btn"
                icon={isViewerFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setIsViewerFullScreen(!isViewerFullScreen)}
                size="small"
                style={{ position: "absolute", top: 10, right: 10, zIndex: 30 }}
              />
            </Tooltip>

            {/* iframe fills parent div 100% */}
            <Iframe
              url={ohifBaseUrl ? `${ohifBaseUrl}?StudyInstanceUIDs=${studyId}` : undefined}
              width="100%"
              height="100%"
              display="block"
              position="relative"
              styles={{ border: "none", display: "block" }}
            />
          </div>

          {/* ══ Report editor pane ══ */}
          {!isViewerFullScreen && (
            <div style={{
              flex: "0 0 38%",
              height: "100%",
              overflowY: "auto",
              background: "#07101f",
              position: "relative",
            }}>
              <div style={{ padding: "16px 16px 80px", display: "flex", flexDirection: "column", gap: 14 }}>

                {/* ── Patient info card ── */}
                <PatientInfoCard study={studyDetails} userDetails={userDetails} role={role} />

                {/* ── Template selector ── */}
                <div style={{
                  background: "rgba(14,22,41,0.8)",
                  border: "1px solid rgba(99,102,241,0.25)",
                  borderRadius: 10,
                  padding: "12px 14px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span className="rdy-section-label">Choose Template</span>
                    <Tooltip title="Study Remarks / History">
                      <button
                        onClick={() => setHistoryModal({ isModalOpen: true, studyKey: studyId })}
                        style={{
                          background: studyRemark?.length > 0 ? "rgba(37,211,102,0.12)" : "rgba(99,102,241,0.1)",
                          border: `1px solid ${studyRemark?.length > 0 ? "rgba(37,211,102,0.4)" : "rgba(99,102,241,0.3)"}`,
                          borderRadius: 6, width: 30, height: 30, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: studyRemark?.length > 0 ? "#25D366" : "#818cf8",
                          fontSize: 15,
                        }}
                      >
                        <WechatOutlined />
                      </button>
                    </Tooltip>
                  </div>
                  <Select
                    showSearch
                    className="rdy-select"
                    value={selectedTemplate}
                    style={{ width: "100%" }}
                    disabled={disableButton}
                    onChange={handleTemplateChange}
                    placeholder="Select a template"
                    dropdownStyle={{ background: "#0e1629", border: "1px solid rgba(99,102,241,0.3)" }}
                  >
                    {templateOptions?.template?.data?.map((template) => (
                      <Select.Option key={template?.id} value={template?.value}>
                        {template?.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* ── Report editor ── */}
                <div style={{
                  background: "rgba(14,22,41,0.8)",
                  border: "1px solid rgba(99,102,241,0.25)",
                  borderRadius: 10,
                  padding: "12px 14px",
                  display: "flex", flexDirection: "column", gap: 10,
                }}>
                  {/* Header row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span className="rdy-section-label">Report Editor</span>
                    {modality && (
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        background: getModalityStyle(modality).bg,
                        color: getModalityStyle(modality).color,
                        border: `1px solid ${getModalityStyle(modality).border}`,
                        borderRadius: 4, padding: "2px 10px",
                        letterSpacing: "0.06em",
                      }}>
                        {modality}
                      </span>
                    )}
                  </div>

                  {/* AI Toolbar */}
                  {(features?.aiDictation !== false || features?.aiStructure !== false || features?.aiImpression !== false) && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {features?.aiDictation !== false && (
                        <Tooltip title={isRecording ? "Stop recording" : "Voice dictate → auto-structure + impression"}>
                          <Button
                            icon={<AudioOutlined />}
                            disabled={disableButton}
                            onClick={handleDictate}
                            className={`rdy-ai-btn ${isRecording ? "recording-btn rdy-dictate-active" : ""}`}
                            size="small"
                          >
                            {isRecording ? "Recording…" : "Dictate"}
                          </Button>
                        </Tooltip>
                      )}

                      {/* Pipeline status indicator */}
                      {(isTranscribing || isStructuring || isGeneratingImpression) && (
                        <span style={{
                          display: "flex", alignItems: "center", gap: 5,
                          fontSize: 11, color: "#818cf8", fontStyle: "italic",
                        }}>
                          <Spin size="small" style={{ color: "#6366f1" }} />
                          {aiStep}
                        </span>
                      )}

                      {features?.aiStructure !== false && (
                        <Tooltip title="Structure current editor text into sections">
                          <Button
                            icon={<ThunderboltOutlined />}
                            disabled={disableButton || isTranscribing || isStructuring || isGeneratingImpression}
                            onClick={handleAiStructure}
                            loading={isStructuring}
                            className="rdy-ai-btn"
                            size="small"
                          >
                            AI Structure
                          </Button>
                        </Tooltip>
                      )}

                      {features?.aiImpression !== false && (
                        <Tooltip title="Generate Impression from Findings">
                          <Button
                            icon={<BulbOutlined />}
                            disabled={disableButton || isTranscribing || isStructuring || isGeneratingImpression}
                            onClick={handleAiImpression}
                            loading={isGeneratingImpression}
                            className="rdy-ai-btn"
                            size="small"
                          >
                            AI Impression
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/* Quill editor */}
                  <div className="rdy-quill">
                    <ReactQuill
                      ref={quillRef}
                      className="newtable"
                      value={editorValue}
                      onChange={(value) => { setEditorValue(value); setFinalHtml(value); }}
                      theme="snow"
                      modules={modules}
                    />
                  </div>
                </div>

                {/* ── Submit button ── */}
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 4 }}>
                  <Button
                    className="rdy-submit-btn"
                    disabled={disableButton}
                    loading={isLoading}
                    onClick={handleSave}
                    style={{ minWidth: 160, height: 40, fontSize: 14 }}
                  >
                    Submit Report
                  </Button>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>

      {/* History modal */}
      <Modal
        title="History"
        className="ModalFooterHide"
        open={historyModal.isModalOpen}
        onOk={handleOkTextModal}
        onCancel={handleCancelTextModal}
        destroyOnClose
        width={450}
      >
        <StudyTextModal
          studyPrimaryKey={studyInfoId}
          handleOk={handleOkTextModal}
          fetchData={null}
        />
      </Modal>

      {/* Critical findings modal */}
      <Modal
        title={
          <span style={{ color: "#ff4d4f" }}>
            <WarningOutlined style={{ marginRight: 8 }} />
            Critical Findings Detected
          </span>
        }
        open={criticalModal.open}
        onCancel={() => setCriticalModal((s) => ({ ...s, open: false }))}
        footer={[
          <Button key="cancel" onClick={() => setCriticalModal((s) => ({ ...s, open: false }))}>
            Cancel
          </Button>,
          <Button
            key="acknowledge"
            type="primary"
            danger
            loading={isLoading}
            onClick={async () => {
              setCriticalModal((s) => ({ ...s, open: false }));
              await performSave();
            }}
          >
            I acknowledge — proceed
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 12 }}>{criticalModal.message}</p>
        {criticalModal.matches.length > 0 && (
          <>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Matched critical keywords:</p>
            <ul style={{ paddingLeft: 20 }}>
              {criticalModal.matches.map((m) => (
                <li key={m} style={{ color: "#ff4d4f", fontWeight: "bold" }}>{m}</li>
              ))}
            </ul>
          </>
        )}
      </Modal>
    </>
  );
};

export default Viewer;
